import { http } from '@/lib/http';
import { menuSlugs, sectionSlugs, itemSlugs, type Locale } from '@/lib/localized-paths';

export type RawNavigationNode = {
  id: number;
  title: string;
  parent_id: number | null;
  order: number | string | null;
  slug: string;
  show_sidebar?: boolean | number | string | null;
  /**
   * Per-locale slug/title. The backend returns the node's own locale in `slug`
   * and the sibling locale here, so paths can be built for either locale.
   */
  translations?: Record<string, { title?: string; slug?: string }> | null;
};

export type NavigationNode = RawNavigationNode & {
  children: NavigationNode[];
  path: string;
};

type NavigationResponse = {
  data: RawNavigationNode[];
};

function normalizeSlug(slug?: string | null) {
  return (slug ?? '').trim().replace(/^\/+|\/+$/g, '');
}

/**
 * Resolves the slug of a node for a specific locale.
 *
 * The navigation API is locale-scoped: `slug` is already localized for the
 * requested `lang`, while `translations` holds the sibling locale. Falling back
 * to `slug` keeps nodes without translations working.
 */
function localizedSlug(node: RawNavigationNode, lang: string) {
  const translated = node.translations?.[lang]?.slug;
  return normalizeSlug(translated || node.slug);
}

function isRoot(parentId: RawNavigationNode['parent_id']) {
  if (parentId === null || parentId === undefined) return true;
  return Number(parentId) < 0;
}

function normalizeShowSidebar(value: RawNavigationNode['show_sidebar']) {
  if (typeof value === 'boolean') return value;
  if (value == null) return true;
  return String(value) !== '0' && String(value).toLowerCase() !== 'false';
}

function sortNodes(nodes: NavigationNode[]) {
  nodes.sort((a, b) => {
    const orderDiff =
      Number(a.order ?? Number.MAX_SAFE_INTEGER) -
      Number(b.order ?? Number.MAX_SAFE_INTEGER);
    if (orderDiff !== 0) return orderDiff;
    return a.title.localeCompare(b.title);
  });

  nodes.forEach((child) => sortNodes(child.children));
}

function assignPaths(nodes: NavigationNode[], lang: string, parentSegments: string[] = []) {
  nodes.forEach((node) => {
    const slug = localizedSlug(node, lang);
    const segments = slug ? [...parentSegments, slug] : [...parentSegments];
    node.path = segments.length ? `/${segments.join('/')}` : '/';
    assignPaths(node.children, lang, segments);
  });
}

function buildTree(items: RawNavigationNode[], lang: string): NavigationNode[] {
  const map = new Map<number, NavigationNode>();

  items.forEach((item) => {
    map.set(item.id, {
      ...item,
      order:
        typeof item.order === 'number'
          ? item.order
          : Number(item.order ?? Number.MAX_SAFE_INTEGER),
      slug: normalizeSlug(item.slug),
      show_sidebar: normalizeShowSidebar(item.show_sidebar),
      children: [],
      path: '/',
    });
  });

  const roots: NavigationNode[] = [];

  map.forEach((node) => {
    if (!node || isRoot(node.parent_id)) {
      roots.push(node);
      return;
    }

    const parent = map.get(Number(node.parent_id));
    if (parent && parent !== node) {
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  });

  sortNodes(roots);
  assignPaths(roots, lang);

  return roots;
}

/**
 * Fetches navigation menu data from API and returns a hierarchical tree structure
 * @param lang - Language code for localization
 * @returns Promise resolving to array of navigation nodes with children
 */
export async function fetchNavigationMenu(lang: string): Promise<NavigationNode[]> {
  const res = await http.get<NavigationResponse>(`/api/navigations?lang=${lang}`);
  return buildTree(res.data?.data ?? [], lang);
}

/**
 * Maps a localized slug segment to its canonical (EN) slug.
 *
 * The navigation API does not localize every node — e.g. the procurement menu
 * keeps the slug `procurement-information` in both locales — while the frontend
 * still links to `/id/informasi-pengadaan/...`. Normalizing each segment to its
 * canonical slug lets a URL be matched against the tree regardless of which
 * localized variant the backend happens to return.
 */
function canonicalSegment(locale: Locale, segment: string) {
  const maps = [menuSlugs[locale], sectionSlugs[locale], itemSlugs[locale]];
  for (const map of maps) {
    for (const [canonical, localized] of Object.entries(map)) {
      if (localized === segment) return canonical;
    }
  }
  return segment;
}

/** Canonical form of a path, used to compare URLs against navigation paths. */
export function canonicalPath(locale: Locale, path: string) {
  const segments = path.split('/').filter(Boolean).map((s) => canonicalSegment(locale, s));
  return segments.length ? `/${segments.join('/')}` : '/';
}

export function findNodeByPath(
  nodes: NavigationNode[],
  path: string
): NavigationNode | undefined {
  for (const node of nodes) {
    if (node.path === path) return node;
    const found = findNodeByPath(node.children, path);
    if (found) return found;
  }
  return undefined;
}

export function findNodeById(
  nodes: NavigationNode[],
  id: number
): NavigationNode | undefined {
  for (const node of nodes) {
    if (node.id === id) return node;
    const found = findNodeById(node.children, id);
    if (found) return found;
  }
  return undefined;
}

/**
 * Resolves the equivalent path of `path` in `targetLocale`.
 *
 * The node is located in the source tree by canonical path (so localized slugs
 * that the backend does not translate still match), then the target path is
 * taken from the same node id in the target tree. This guarantees every segment
 * is localized consistently instead of producing mixed paths such as
 * /id/informasi-pengadaan/list-of-planned-procurement.
 */
export async function resolveLocalizedPath(
  path: string,
  sourceLocale: Locale,
  targetLocale: Locale
): Promise<string | null> {
  try {
    const [sourceTree, targetTree] = await Promise.all([
      fetchNavigationMenu(sourceLocale),
      fetchNavigationMenu(targetLocale),
    ]);

    const canonical = canonicalPath(sourceLocale, path);
    const node =
      findNodeByPath(sourceTree, path) ??
      findNodeByPath(sourceTree, canonical) ??
      findNodeByPath(targetTree, canonicalPath(targetLocale, path));

    if (!node) return null;

    const targetNode =
      findNodeById(targetTree, node.id) ?? findNodeByPath(targetTree, canonical);
    return targetNode?.path ?? null;
  } catch {
    return null;
  }
}
